import { useSelector } from 'react-redux';
import { BillingSelectors } from 'reactApp/modules/billing/billing.selectors';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { BuySubscriptionSelectors } from 'reactApp/modules/buySubscription/buySubscription.module';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { cheapestProductIDs } from 'reactApp/modules/products/products.const';
import { getPretextPeriodForProduct } from 'reactApp/modules/products/products.helpers';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { isGraceSubscription } from 'reactApp/sections/SubscriptionsPage/SubscriptionsPage.helpers';
import type { RootState } from 'reactApp/store';
import type { Product } from 'reactApp/types/Billing';
import { usePromoPeriod } from 'reactApp/ui/PaymentFooter/usePromoPeriod';
import { getPeriodName, getPeriodNameAsWord, isHourPeriod, isYear2Period } from 'reactApp/utils/Period';
import { formatPrice } from 'reactApp/utils/priceHelpers';

export const useProduct = ({
    productId,
    product: incomingProduct,
    subscription,
}: {
    productId: string;
    product?: Product;
    subscription?: BillingDataSubscriptionInfo;
}) => {
    const renewalCost = useSelector(BillingSelectors.getRenewalCost);

    const discountOutflowPercent = useSelector(BuySubscriptionSelectors.getDiscountInOutflowPercent);

    // TODO: если id невалидный, то product будет undefined и всё посыпется. Стоит предусмотреть и убрать as Product
    const product = useSelector((state: RootState) => incomingProduct || ProductsSelectors.getProductById(state, productId)) as Product;
    const price = product?.discountPrice || product?.price || 0;
    const renewalPrice = renewalCost || price;
    const space = product?.space?.value || '';

    const formattedRenewalPrice = `${formatPrice(renewalPrice)}\u00A0₽`;
    const fullPrice = `${formatPrice(product?.price)}\u00A0₽`;

    const promoPeriod = usePromoPeriod({ product, subscription });

    const isPhone = useSelector(EnvironmentSelectors.isPhone);

    const trialString =
        // Подписка наличествует только в сценарии продления, а при продлении мы берем полную/дисконтную стоимость
        !subscription &&
        // Далее ориентируемся на продукт
        product?.hasTrial &&
        Boolean(product?.trialPeriod) &&
        !isPhone
            ? `Для проверки карты мы спишем и сразу же вернем 1 ₽. `
            : '';

    const isGrace = isGraceSubscription(subscription);
    if (isGrace) {
        return {
            text: 'После доплаты подписка продлится автоматически.',
        };
    }

    if (promoPeriod && price && !discountOutflowPercent) {
        return {
            text: `${trialString}${getPretextPeriodForProduct(promoPeriod)} ${getPeriodNameAsWord(
                promoPeriod,
                true
            )} оплата составит ${fullPrice} ${isYear2Period(product.period) ? 'за' : 'в'} ${getPeriodName(product.period)}.`,
        };
    }

    // tempexp_16598-start
    if (Object.values(cheapestProductIDs).includes(productId)) {
        return {
            text: 'Подписка «Без рекламы» продлевается автоматически.',
        };
    }
    // tempexp_16598-end

    if (isHourPeriod(product.period)) {
        return {
            text: 'Это оплата загрузки одного файла — подписка Mail Space не подключится.',
            isOneTime: true,
        };
    }

    return {
        text: `${trialString}Подписка на ${space} ${
            renewalPrice && `за ${formattedRenewalPrice}`
        } продлевается автоматически через ${getPeriodName(product.period)} на аналогичный период.`,
    };
};
